// Identity rules shared by the session, the router and the sign-in screens (0022). Pure
// functions only, so the mode-dependent behaviour is unit-tested without a browser.
//
// Two build modes (VITE_PTW_MODE):
//   * 'synthetic_demo': the persona model. Each persona is its own anonymous sign-in bound
//     on the server to one fictional person; a persona menu switches between them.
//   * 'operational' / 'staging': real accounts. One person per browser session, signed in
//     with a one-time code emailed to their own invited address. No persona menu, no /demo,
//     no join codes; nobody can pick another person's signing identity.
// Signatures are always made by the session's own server-side identity (auth.uid() ->
// ptw.user_accounts -> person): the browser never names the signer.

export type PtwMode = 'synthetic_demo' | 'staging' | 'operational'

/**
 * The build mode. Unset keeps the demo (the existing builds). Anything other than the exact
 * demo value is a real-account build, so a mistyped value never opens the persona flow.
 */
export function parseMode(raw: string | undefined | null): PtwMode {
  const v = (raw ?? '').trim()
  if (v === '' || v === 'synthetic_demo') return 'synthetic_demo'
  if (v === 'staging') return 'staging'
  return 'operational'
}

export function isAccountMode(mode: PtwMode): boolean {
  return mode !== 'synthetic_demo'
}

/** A sign-in lasts one hour, whatever the person is doing (Razan, 2026-11-04). */
export const SIGN_IN_LIMIT_MS = 60 * 60_000
/** The warning appears this long before the end. */
export const SIGN_IN_WARNING_MS = 5 * 60_000

/**
 * Milliseconds left on a sign-in made at signedInAt (<= 0 once it has ended). No recorded
 * time counts as ended, so a session of unknown age is asked to sign in again.
 */
export function remainingMs(signedInAt: number | null, now: number): number {
  if (signedInAt === null || !Number.isFinite(signedInAt)) return 0
  return signedInAt + SIGN_IN_LIMIT_MS - now
}

/** The signed-in real account in this browser (account mode). */
export type AccountIdentity = { userId: string; email: string; signedInAt: number }

export type IdentityState = {
  mode: PtwMode
  sandboxCode: string | null
  activePersona: string | null
  account: AccountIdentity | null
}

/**
 * Cache and storage identity: a different identity sees different data. Demo: sandbox +
 * persona (as before). Accounts: the auth user id, never the email or a display name.
 */
export function identityKey(s: IdentityState): string | null {
  if (isAccountMode(s.mode)) return s.account ? `user:${s.account.userId}` : null
  return s.activePersona ? `${s.sandboxCode ?? '-'}:${s.activePersona}` : null
}

/** Where an unsigned visitor of a data page goes: null when the page may render. */
export function signInRoute(s: IdentityState): '/demo' | '/sign-in' | null {
  if (identityKey(s) !== null) return null
  return isAccountMode(s.mode) ? '/sign-in' : '/demo'
}

/** Routes that exist in a mode. The demo page and its join codes exist only in the demo. */
export function routeAvailable(mode: PtwMode, path: '/demo' | '/sign-in'): boolean {
  return path === '/demo' ? !isAccountMode(mode) : isAccountMode(mode)
}

/** Only an in-app path is followed after sign-in (never another origin or the sign-in page). */
export function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\') || raw.startsWith('/sign-in')) return '/'
  return raw
}

export function normaliseEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

export function emailProblem(raw: string): string | null {
  const e = normaliseEmail(raw)
  if (e === '') return 'Enter your email address.'
  if (e.length > 254 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return 'Enter a full email address, for example name@badreor.com.'
  return null
}

/** The emailed code: six digits (spaces and dashes typed by the person are ignored). */
export function normaliseCode(raw: string): string {
  return raw.replace(/[\s-]/g, '')
}

export function codeProblem(raw: string): string | null {
  const c = normaliseCode(raw)
  if (c === '') return 'Enter the 6-digit code from the email.'
  if (!/^\d{6}$/.test(c)) return 'The code is 6 digits.'
  return null
}

/** Why a sign-in did not give access. */
export type SignInProblem =
  | 'NOT_INVITED'
  | 'INVITATION_EXPIRED'
  | 'INVITATION_INVALID'
  | 'SUSPENDED'
  | 'DISABLED'
  | 'CODE_INVALID'
  | 'RATE_LIMITED'
  | 'ANONYMOUS'
  | 'EMAIL_UNCONFIRMED'
  | 'NETWORK'
  | 'UNKNOWN'

export const SIGN_IN_MESSAGES: Record<SignInProblem, { title: string; body: string }> = {
  NOT_INVITED: {
    title: 'This email address is not invited',
    body: 'Only invited people can sign in. Check the address, or ask your account administrator to invite you.',
  },
  INVITATION_EXPIRED: {
    title: 'Your invitation has expired',
    body: 'Ask your account administrator to send the invitation again, then sign in with a new code.',
  },
  INVITATION_INVALID: {
    title: 'Your invitation can no longer be used',
    body: 'The administrator who invited you no longer manages accounts, or you already have an account. Ask an account administrator.',
  },
  SUSPENDED: {
    title: 'Your account is suspended',
    body: 'You cannot read or sign anything while it is suspended. Ask your account administrator to reactivate it.',
  },
  DISABLED: {
    title: 'Your account is disabled',
    body: 'A disabled account cannot be used again. Ask your account administrator if you need a new invitation.',
  },
  CODE_INVALID: {
    title: 'The code is wrong or has expired',
    body: 'Check the latest email and enter its code, or ask for a new code. Each code works once and only for a short time.',
  },
  RATE_LIMITED: {
    title: 'Too many codes asked for',
    body: 'Wait a minute before asking for another code. The last code we sent still works until it expires.',
  },
  ANONYMOUS: {
    title: 'Sign in with your email address',
    body: 'This system has no anonymous or shared sign-in. Use your own invited email address.',
  },
  EMAIL_UNCONFIRMED: {
    title: 'Your email address is not confirmed yet',
    body: 'Enter the code from the email to confirm it.',
  },
  NETWORK: {
    title: 'Could not reach the server',
    body: 'Check your connection and try again.',
  },
  UNKNOWN: {
    title: 'Could not sign in',
    body: 'Try again. If it keeps failing, ask your account administrator.',
  },
}

/** The account state ptw_account_activate reports, as a sign-in problem (null = active). */
export function activationProblem(status: string): SignInProblem | null {
  switch (status) {
    case 'active':
      return null
    case 'suspended':
      return 'SUSPENDED'
    case 'disabled':
    case 'pending':
      return 'DISABLED'
    case 'not_invited':
      return 'NOT_INVITED'
    case 'invitation_expired':
      return 'INVITATION_EXPIRED'
    case 'invitation_invalid':
      return 'INVITATION_INVALID'
    case 'anonymous':
      return 'ANONYMOUS'
    case 'email_unconfirmed':
      return 'EMAIL_UNCONFIRMED'
    default:
      return 'UNKNOWN'
  }
}

/**
 * A Supabase Auth error from asking for or verifying a code, as a sign-in problem. The
 * before-user-created hook (0022) refuses uninvited addresses with a message starting
 * NOT_INVITED; Auth's own codes cover wrong/expired codes and rate limits.
 */
export function authErrorProblem(e: unknown): SignInProblem {
  const err = (e ?? {}) as { code?: unknown; message?: unknown; status?: unknown; name?: unknown }
  const code = typeof err.code === 'string' ? err.code : ''
  const message = typeof err.message === 'string' ? err.message : ''
  if (/NOT_INVITED/.test(message) || code === 'signup_disabled' || code === 'otp_disabled' || code === 'user_not_found') return 'NOT_INVITED'
  if (code === 'otp_expired' || /token has expired|invalid.*(otp|token)|otp.*invalid/i.test(message)) return 'CODE_INVALID'
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit' || err.status === 429 || /rate limit/i.test(message)) return 'RATE_LIMITED'
  if (code === 'user_banned') return 'DISABLED'
  if (err.name === 'AuthRetryableFetchError' || /fetch|network/i.test(message)) return 'NETWORK'
  return 'UNKNOWN'
}

export class SignInError extends Error {
  readonly problem: SignInProblem
  constructor(problem: SignInProblem) {
    super(SIGN_IN_MESSAGES[problem].title)
    this.problem = problem
  }
}

/** Account state named by PW004 ACCOUNT_INACTIVE (0022 detail), if any. */
export function inactiveAccountState(detail: unknown): 'suspended' | 'disabled' | 'none' | null {
  const d = detail as { account_status?: unknown } | null
  const s = d && typeof d === 'object' ? d.account_status : undefined
  if (s === 'suspended' || s === 'disabled' || s === 'none') return s
  if (s === 'pending') return 'disabled'
  return null
}
